import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import AuthLayout from "@container/layout/auth";
import { Card, CardContent } from "@component/card";
import { Button } from "@component/button";
import { InterviewPlayer } from "@component/interview-player";
import SWEExperienceSurvey from "@container/eligibility-check-job";
import { getListJobApplicant, getCandidateActivityHistories } from "@service/job";
import { getApplicantDisplayCV } from "@service/cv";
import { getUserFormSubmission, getUserInterviewSubmission } from "@service/assessment";
import { getVideo } from "@service/interview";
import { getCandidateIssues, getInterviewTranscription } from "@service/admin";
import { JobApplicantItem } from "@type/job";
import { CandidateActivityGroup, CandidateInterviewStatus } from "@type/candidate";
import { UserForm, UserInterview } from "@type/assessments";
import { ApplicantCVDisplay } from "@type/cv";
import { InterviewVideo } from "@type/videoInterview";
import {
    AdminCandidateIssues,
    AdminBugReport,
    AdminInterviewFeedback,
    AdminProblemActivity,
    AdminProblemApplication,
    AdminDeviceMeta,
    AdminInterviewTranscription,
} from "@type/admin";
import { interviewStatusNone } from "@constant/app";
import { catchErrorLog } from "@util/sentry";
import { compareIsoDatesDesc, parseFormat, formatShortDate, formatTimeAgo } from "@util/date";
import { convertMarkdownToHtml, convertCapitalizeWords, convertToLowerSnakeCase, getApplicantStatusBadge, renameAssessmentToScreening } from "@util/string";
import { cn } from "@util/tailwind";
import { mapApplicantStage } from "@constant/app";
import { Badge } from "@component/badge";
import {
    ArrowLeftIcon,
    ArrowRightLeft,
    BriefcaseIcon,
    BuildingIcon,
    ClipboardCopyIcon,
    CheckIcon,
    ChevronDown,
    ChevronRight,
    HistoryIcon,
    VideoIcon,
    FileTextIcon,
    ShieldCheckIcon,
    AlertCircleIcon,
    Link2OffIcon,
    FileIcon,
    DownloadIcon,
    UserIcon,
    UserCheck,
    Settings,
    MailIcon,
    BugIcon,
    MessageSquareWarningIcon,
    StarIcon,
    ActivityIcon,
    HeartPulseIcon,
    MessagesSquareIcon,
    BotIcon,
} from "lucide-react";

interface NavState {
    name?: string;
    email?: string;
    phone?: string;
    country_code?: string;
}

function CopyButton({ value, label }: { value: string; label?: string }) {
    const [copied, setCopied] = useState(false);
    if (!value) return null;
    return (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigator.clipboard.writeText(value).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1200);
                });
            }}
            className="inline-flex items-center gap-1 text-xs font-mono text-neutral-700 bg-neutral-100 hover:bg-neutral-200 px-2 py-1 rounded transition-colors"
            title={label ? `Copy ${label}` : "Copy"}
        >
            <span className="truncate max-w-[180px]">{value}</span>
            {copied ? <CheckIcon className="w-3 h-3 text-green-600" /> : <ClipboardCopyIcon className="w-3 h-3" />}
        </button>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium mb-0.5">{label}</p>
            <div className="text-sm text-gray-900">{children}</div>
        </div>
    );
}

function SectionCard({
    icon: Icon,
    title,
    children,
    rightAccessory,
}: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    children: React.ReactNode;
    rightAccessory?: React.ReactNode;
}) {
    return (
        <Card>
            <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-blue-600" />
                        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    </div>
                    {rightAccessory}
                </div>
                {children}
            </CardContent>
        </Card>
    );
}

function SummaryChip({
    label,
    value,
    tone,
}: {
    label: string;
    value: number;
    tone: "neutral" | "amber" | "red" | "blue";
}) {
    const toneCls =
        tone === "red"
            ? "bg-red-50 border-red-100 text-red-700"
            : tone === "amber"
                ? "bg-amber-50 border-amber-100 text-amber-800"
                : tone === "blue"
                    ? "bg-blue-50 border-blue-100 text-blue-700"
                    : "bg-neutral-50 border-neutral-200 text-neutral-700";
    const valueCls = value > 0 ? "" : "opacity-60";
    return (
        <div className={cn("border rounded-lg p-3 flex items-baseline justify-between gap-2", toneCls, valueCls)}>
            <span className="text-[11px] uppercase tracking-wider font-medium">{label}</span>
            <span className="text-lg font-bold tabular-nums">{value}</span>
        </div>
    );
}

function IssueSummaryRow({ issues, isLoading }: { issues: AdminCandidateIssues | null; isLoading: boolean }) {
    if (isLoading) {
        return (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-14 bg-neutral-100 rounded-lg animate-pulse" />
                ))}
            </div>
        );
    }
    if (!issues) return null;
    const s = issues.summary;
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <SummaryChip label="Bug Reports" value={s.total_bug_reports} tone={s.total_bug_reports > 0 ? "red" : "neutral"} />
            <SummaryChip label="Interview Feedback" value={s.total_interview_feedback} tone={s.total_interview_feedback > 0 ? "amber" : "neutral"} />
            <SummaryChip label="Incomplete Interviews" value={s.total_incomplete_interviews} tone={s.total_incomplete_interviews > 0 ? "amber" : "neutral"} />
            <SummaryChip label="Video Errors" value={s.total_video_processing_errors} tone={s.total_video_processing_errors > 0 ? "red" : "neutral"} />
            <SummaryChip label="Drop-offs" value={s.total_drop_offs} tone={s.total_drop_offs > 0 ? "amber" : "neutral"} />
            <SummaryChip label="Problem Activities" value={s.total_problem_activities} tone={s.total_problem_activities > 0 ? "amber" : "neutral"} />
        </div>
    );
}

function DeviceMetaCompact({ meta }: { meta: AdminDeviceMeta | null | undefined }) {
    if (!meta) return null;
    const items: { label: string; value: string }[] = [];
    const browserBits = [meta.browser, meta.browser_version].filter(Boolean).join(" ");
    if (browserBits) items.push({ label: "Browser", value: browserBits });
    if (meta.os) items.push({ label: "OS", value: String(meta.os) });
    const deviceBits = [meta.device_vendor, meta.device_model].filter(Boolean).join(" ");
    if (deviceBits) items.push({ label: "Device", value: deviceBits });
    if (meta.audio_device?.label) items.push({ label: "Mic", value: meta.audio_device.label });
    if (meta.video_device?.label) items.push({ label: "Camera", value: meta.video_device.label });
    if (items.length === 0) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
            {items.map((it) => (
                <div key={it.label} className="flex items-baseline gap-2">
                    <span className="text-neutral-500 min-w-[64px]">{it.label}</span>
                    <span className="text-gray-800 truncate">{it.value}</span>
                </div>
            ))}
        </div>
    );
}

function BugReportsSection({ reports }: { reports: AdminBugReport[] }) {
    return (
        <SectionCard
            icon={BugIcon}
            title={`Bug Reports (${reports.length})`}
        >
            <div className="space-y-3">
                {reports.map((r) => (
                    <div key={r.id} className="border border-red-100 bg-red-50/40 rounded-lg p-4 space-y-2">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900">
                                {r.bug_type || "Unknown bug"}
                            </p>
                            <p className="text-[11px] text-neutral-500 font-mono">
                                {parseFormat(r.created_at, "D MMM YYYY HH:mm")}
                            </p>
                        </div>
                        {r.bug_description && (
                            <p className="text-sm text-gray-700 whitespace-pre-line">{r.bug_description}</p>
                        )}
                        <DeviceMetaCompact meta={r.meta} />
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

function InterviewFeedbackSection({ feedback }: { feedback: AdminInterviewFeedback[] }) {
    return (
        <SectionCard
            icon={MessageSquareWarningIcon}
            title={`Interview Feedback (${feedback.length})`}
        >
            <div className="space-y-3">
                {feedback.map((f) => (
                    <div key={f.id} className="border border-amber-100 bg-amber-50/40 rounded-lg p-4 space-y-2">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-gray-900">{f.interview || "Interview"}</p>
                                {f.is_incomplete && (
                                    <span className="text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                        Incomplete
                                    </span>
                                )}
                                {typeof f.feedback_rate === "number" && (
                                    <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                        <StarIcon className="w-3 h-3" /> {f.feedback_rate}/5
                                    </span>
                                )}
                                {f.feedback_category && (
                                    <span className="text-[10px] uppercase tracking-wider font-medium text-neutral-600 bg-neutral-100 px-1.5 py-0.5 rounded">
                                        {f.feedback_category}
                                    </span>
                                )}
                            </div>
                            <p className="text-[11px] text-neutral-500 font-mono">
                                {parseFormat(f.created_at, "D MMM YYYY HH:mm")}
                            </p>
                        </div>
                        {f.feedback_comment && (
                            <p className="text-sm text-gray-700 whitespace-pre-line">{f.feedback_comment}</p>
                        )}
                        <DeviceMetaCompact meta={f.feedback_metadata} />
                        {f.video_processing_errors.length > 0 && (
                            <div className="text-xs space-y-0.5">
                                <p className="font-medium text-red-700">
                                    Video processing errors ({f.video_processing_errors.length})
                                </p>
                                <ul className="font-mono text-[11px] text-red-700 space-y-0.5">
                                    {f.video_processing_errors.map((err, i) => (
                                        <li key={i}>
                                            <span className="text-red-500">#{err.segment_index}</span> {err.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {f.conversation_id && (
                            <div className="flex items-center gap-2 pt-1">
                                <span className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">Conversation ID</span>
                                <CopyButton value={f.conversation_id} label="conversation id" />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

function ProblemActivitiesSection({ activities }: { activities: AdminProblemActivity[] }) {
    return (
        <SectionCard
            icon={ActivityIcon}
            title={`Problem Activities (${activities.length})`}
        >
            <div className="space-y-3">
                {activities.map((a) => (
                    <div key={a.id} className="border-l-2 border-amber-300 pl-4 py-1">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900">{a.action_header}</p>
                            <p className="text-[11px] text-neutral-500 font-mono">
                                {parseFormat(a.action_at, "D MMM YYYY HH:mm")}
                            </p>
                        </div>
                        {a.action_description && (
                            <p
                                className="text-sm text-neutral-600 mt-0.5"
                                dangerouslySetInnerHTML={{ __html: a.action_description }}
                            />
                        )}
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

const FUNNEL_STATUS_LABEL: Record<string, string> = {
    uploads: "Uploads",
    applied: "Applied",
    pending_assessment: "Screening In Progress",
    to_review: "Ready for Review",
    offers: "Offers",
    closed: "Closed",
};

function getFunnelStatusLabel(value: string | null | undefined): string {
    if (!value) return "—";
    return FUNNEL_STATUS_LABEL[value] || convertCapitalizeWords(value);
}

function TranscriptionStatusBadge({ status }: { status: string }) {
    const tone =
        status === "ok"
            ? "bg-green-50 text-green-700 border-green-100"
            : status === "empty"
                ? "bg-neutral-50 text-neutral-600 border-neutral-200"
                : "bg-red-50 text-red-700 border-red-100";
    return (
        <span className={cn("text-[10px] uppercase tracking-wider font-medium border rounded px-1.5 py-0.5", tone)}>
            {status}
        </span>
    );
}

function InterviewTranscriptionPanel({
    transcription,
    isLoading,
    error,
}: {
    transcription: AdminInterviewTranscription | null;
    isLoading: boolean;
    error: string;
}) {
    if (isLoading) {
        return (
            <div className="border-t border-neutral-100 pt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <MessagesSquareIcon className="w-4 h-4 text-blue-600" />
                    Transcription
                </div>
                <div className="space-y-2">
                    <div className="h-3 w-3/4 bg-neutral-100 rounded animate-pulse" />
                    <div className="h-3 w-2/3 bg-neutral-100 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-neutral-100 rounded animate-pulse" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="border-t border-neutral-100 pt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <MessagesSquareIcon className="w-4 h-4 text-blue-600" />
                    Transcription
                </div>
                <p className="text-xs text-neutral-500">{error}</p>
            </div>
        );
    }

    if (!transcription) return null;

    return (
        <div className="border-t border-neutral-100 pt-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <MessagesSquareIcon className="w-4 h-4 text-blue-600" />
                    <h4 className="text-sm font-semibold text-gray-900">
                        Transcription
                    </h4>
                    <TranscriptionStatusBadge status={transcription.transcription_status} />
                </div>
                <div className="flex items-center gap-2 text-[11px] text-neutral-500">
                    <span>
                        {transcription.transcription_message_count} message
                        {transcription.transcription_message_count === 1 ? "" : "s"}
                    </span>
                    {transcription.retake_count > 1 && (
                        <span className="inline-flex items-center text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5">
                            {transcription.retake_count} retakes
                        </span>
                    )}
                    {transcription.is_incomplete && (
                        <span className="inline-flex items-center text-red-700 bg-red-50 border border-red-100 rounded px-1.5 py-0.5">
                            Incomplete
                        </span>
                    )}
                </div>
            </div>

            {transcription.transcription_status === "malformed" && transcription.transcription_raw ? (
                <div className="bg-red-50/50 border border-red-100 rounded-md p-3">
                    <p className="text-[11px] uppercase tracking-wider text-red-700 font-medium mb-1">
                        Raw transcription (failed to parse)
                    </p>
                    <pre className="text-xs text-red-900 whitespace-pre-wrap break-words font-mono max-h-80 overflow-auto">
                        {transcription.transcription_raw}
                    </pre>
                </div>
            ) : transcription.transcription.length === 0 ? (
                <p className="text-sm text-neutral-500">No transcription messages.</p>
            ) : (
                <div className="bg-neutral-50/60 border border-neutral-100 rounded-xl p-4 max-h-[520px] overflow-y-auto">
                    <div className="flex flex-col gap-3">
                        {transcription.transcription.map((msg, i) => {
                            const isAssistant = msg.role === "assistant";
                            const prev = transcription.transcription[i - 1];
                            const isFirstInGroup = !prev || prev.role !== msg.role;
                            return (
                                <div
                                    key={i}
                                    className={cn(
                                        "flex items-end gap-2",
                                        isAssistant ? "justify-start" : "justify-end"
                                    )}
                                >
                                    {isAssistant && (
                                        <div
                                            className={cn(
                                                "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm",
                                                isFirstInGroup
                                                    ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white"
                                                    : "opacity-0"
                                            )}
                                            aria-hidden={!isFirstInGroup}
                                        >
                                            <BotIcon className="w-3.5 h-3.5" />
                                        </div>
                                    )}
                                    <div className={cn("max-w-[78%] flex flex-col", isAssistant ? "items-start" : "items-end")}>
                                        {isFirstInGroup && (
                                            <span
                                                className={cn(
                                                    "text-[10px] uppercase tracking-wider font-medium mb-1 px-1",
                                                    isAssistant ? "text-blue-700" : "text-neutral-500"
                                                )}
                                            >
                                                {isAssistant ? "Interviewer" : msg.role === "user" ? "Candidate" : msg.role}
                                            </span>
                                        )}
                                        <div
                                            className={cn(
                                                "px-3.5 py-2 text-sm whitespace-pre-line shadow-sm",
                                                isAssistant
                                                    ? "bg-white border border-neutral-200 text-gray-900 rounded-2xl rounded-bl-sm"
                                                    : "bg-blue-600 text-white rounded-2xl rounded-br-sm"
                                            )}
                                        >
                                            {msg.content}
                                        </div>
                                    </div>
                                    {!isAssistant && (
                                        <div
                                            className={cn(
                                                "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm",
                                                isFirstInGroup
                                                    ? "bg-gradient-to-br from-neutral-700 to-neutral-800 text-white"
                                                    : "opacity-0"
                                            )}
                                            aria-hidden={!isFirstInGroup}
                                        >
                                            <UserIcon className="w-3.5 h-3.5" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

function DebugInfoSection({ app }: { app: AdminProblemApplication }) {
    const hasDropOff = app.drop_at_stage !== null || app.drop_off_reminder_sent;
    return (
        <SectionCard icon={HeartPulseIcon} title="Debug Info">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Funnel Status">{getFunnelStatusLabel(app.funnel_status)}</Field>
                <Field label="Application ID">
                    <CopyButton value={app.application_id} label="application id" />
                </Field>
                <Field label="AI Interview Attempts">
                    <span className={app.count_ai_interview_attempts > 1 ? "text-amber-700 font-semibold" : ""}>
                        {app.count_ai_interview_attempts}
                    </span>
                </Field>
                <Field label="Last Heartbeat">
                    {app.last_heartbeat_at ? parseFormat(app.last_heartbeat_at, "D MMM YYYY HH:mm:ss") : "—"}
                </Field>
                {hasDropOff && (
                    <>
                        <Field label="Drop at Stage">
                            <span className="text-amber-700 font-semibold">
                                {app.drop_at_stage !== null ? `Stage ${app.drop_at_stage}` : "—"}
                            </span>
                        </Field>
                        <Field label="Drop-off Reminder">
                            {app.drop_off_reminder_sent ? (
                                <span className="text-amber-700 font-medium">Sent</span>
                            ) : (
                                <span className="text-neutral-500">Not sent</span>
                            )}
                        </Field>
                    </>
                )}
                {app.assessment_link && (
                    <Field label="Screening Link">
                        <CopyButton value={app.assessment_link} label="screening link" />
                    </Field>
                )}
            </div>
        </SectionCard>
    );
}

export default function AdminCandidateInvestigatorDetailPage() {
    const { candidateId } = useParams();
    const location = useLocation();
    const navState = (location.state ?? null) as NavState | null;
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedJobId = searchParams.get("job") || "";
    const [debugCopied, setDebugCopied] = useState(false);

    const [cv, setCv] = useState<ApplicantCVDisplay | null>(null);
    const [isLoadingCv, setIsLoadingCv] = useState(true);
    const [applications, setApplications] = useState<JobApplicantItem[] | null>(null);
    const [isLoadingApplications, setIsLoadingApplications] = useState(true);
    const [loadError, setLoadError] = useState<string>("");

    const [activityGroups, setActivityGroups] = useState<CandidateActivityGroup[] | null>(null);
    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [collapsedActivities, setCollapsedActivities] = useState<Record<string, boolean>>({});
    const [interviewSubmission, setInterviewSubmission] = useState<UserInterview | null>(null);
    const [isLoadingInterview, setIsLoadingInterview] = useState(false);
    const [videoInterview, setVideoInterview] = useState<InterviewVideo | undefined>(undefined);
    const [isLoadingVideo, setIsLoadingVideo] = useState(false);
    const [interviewStatus, setInterviewStatus] = useState<CandidateInterviewStatus | undefined>(undefined);

    const [issues, setIssues] = useState<AdminCandidateIssues | null>(null);
    const [isLoadingIssues, setIsLoadingIssues] = useState(true);
    const [formSubmission, setFormSubmission] = useState<UserForm | null>(null);
    const [isLoadingForm, setIsLoadingForm] = useState(false);

    const [transcription, setTranscription] = useState<AdminInterviewTranscription | null>(null);
    const [isLoadingTranscription, setIsLoadingTranscription] = useState(false);
    const [transcriptionError, setTranscriptionError] = useState<string>("");

    const selectedApplication = useMemo(
        () => applications?.find((a) => a.job_id === selectedJobId) ?? null,
        [applications, selectedJobId]
    );

    useEffect(() => {
        if (!candidateId) return;
        let cancelled = false;

        setIsLoadingCv(true);
        setIsLoadingApplications(true);
        setIsLoadingIssues(true);
        setLoadError("");

        getCandidateIssues(candidateId)
            .then((resp) => {
                if (cancelled) return;
                setIssues(resp.data?.data ?? null);
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Admin investigator: failed to fetch issues ${candidateId}: ${err}`);
                setIssues(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingIssues(false);
            });

        Promise.all([
            getApplicantDisplayCV(candidateId).catch((err) => {
                catchErrorLog(`Admin investigator: failed to fetch CV display ${candidateId}: ${err}`);
                return null;
            }),
            getListJobApplicant(candidateId).catch((err) => {
                catchErrorLog(`Admin investigator: failed to fetch applications ${candidateId}: ${err}`);
                return null;
            }),
        ]).then(([cvResp, appsResp]) => {
            if (cancelled) return;
            const cvData = cvResp?.data?.data;
            setCv(cvData && (cvData.filename || cvData.cv_url) ? cvData : null);
            setIsLoadingCv(false);

            const list = appsResp?.data?.data;
            if (Array.isArray(list)) {
                setApplications(list);
            } else if (!appsResp) {
                setApplications([]);
                setLoadError("Failed to load candidate data. The user ID may be invalid or the API is unreachable.");
            } else {
                setApplications([]);
            }
            setIsLoadingApplications(false);
        });

        return () => {
            cancelled = true;
        };
    }, [candidateId]);

    useEffect(() => {
        if (!selectedApplication || !candidateId) {
            setActivityGroups(null);
            setInterviewSubmission(null);
            setFormSubmission(null);
            setVideoInterview(undefined);
            setInterviewStatus(undefined);
            setTranscription(null);
            setTranscriptionError("");
            setIsLoadingTranscription(false);
            return;
        }

        let cancelled = false;
        const { job_id, applicant_id, assessment_ids } = selectedApplication;
        const applicantIdForApi = applicant_id || candidateId;

        setIsLoadingActivity(true);
        getCandidateActivityHistories(job_id, applicantIdForApi)
            .then((resp) => {
                if (cancelled) return;
                if (resp?.status === 200 && Array.isArray(resp?.data?.data)) {
                    const sortedGroups = [...resp.data.data].sort(
                        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                    );
                    setActivityGroups(sortedGroups);
                } else {
                    setActivityGroups([]);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Admin investigator: activity history failed: ${err}`);
                setActivityGroups([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingActivity(false);
            });

        const interviewId = assessment_ids?.find((a) => a.type === "Interview")?.id;
        if (interviewId) {
            setIsLoadingInterview(true);
            setIsLoadingVideo(true);
            setVideoInterview(undefined);
            setInterviewStatus(undefined);
            setTranscription(null);
            setTranscriptionError("");
            getUserInterviewSubmission(interviewId, candidateId)
                .then((data) => {
                    if (cancelled) return;
                    setInterviewSubmission(data || null);
                    if (data?.conversation_id) {
                        setInterviewStatus({
                            status: data.status || interviewStatusNone,
                            reason: "",
                            updated_at: data.updated_at || "",
                        });
                        getVideo(data.conversation_id)
                            .then((resp) => {
                                if (cancelled) return;
                                if (resp) setVideoInterview(resp);
                            })
                            .catch((err) => {
                                if (cancelled) return;
                                catchErrorLog(`Admin investigator: get interview video failed: ${err}`);
                            })
                            .finally(() => {
                                if (!cancelled) setIsLoadingVideo(false);
                            });

                        setIsLoadingTranscription(true);
                        getInterviewTranscription(data.conversation_id)
                            .then((resp) => {
                                if (cancelled) return;
                                setTranscription(resp.data?.data ?? null);
                            })
                            .catch((err) => {
                                if (cancelled) return;
                                const status = err?.response?.status;
                                if (status === 404) {
                                    setTranscriptionError("No transcription found for this conversation.");
                                } else if (status === 403) {
                                    setTranscriptionError("You don't have permission to view this transcription.");
                                } else {
                                    setTranscriptionError("Failed to load transcription.");
                                }
                                catchErrorLog(`Admin investigator: get transcription failed: ${err}`);
                                setTranscription(null);
                            })
                            .finally(() => {
                                if (!cancelled) setIsLoadingTranscription(false);
                            });
                    } else {
                        setIsLoadingVideo(false);
                    }
                })
                .catch((err) => {
                    if (cancelled) return;
                    catchErrorLog(`Admin investigator: interview submission failed: ${err}`);
                    setInterviewSubmission(null);
                    setIsLoadingVideo(false);
                })
                .finally(() => {
                    if (!cancelled) setIsLoadingInterview(false);
                });
        } else {
            setInterviewSubmission(null);
            setVideoInterview(undefined);
            setInterviewStatus(undefined);
            setIsLoadingVideo(false);
            setTranscription(null);
            setTranscriptionError("");
            setIsLoadingTranscription(false);
        }

        const formId = assessment_ids?.find((a) => a.type === "Form")?.id;
        if (formId) {
            setIsLoadingForm(true);
            getUserFormSubmission(formId, candidateId)
                .then((data) => {
                    if (!cancelled) setFormSubmission(data || null);
                })
                .catch((err) => {
                    if (cancelled) return;
                    catchErrorLog(`Admin investigator: form submission failed: ${err}`);
                    setFormSubmission(null);
                })
                .finally(() => {
                    if (!cancelled) setIsLoadingForm(false);
                });
        } else {
            setFormSubmission(null);
        }

        return () => {
            cancelled = true;
        };
    }, [selectedApplication, candidateId]);

    const selectApplication = (jobId: string) => {
        const next = new URLSearchParams(searchParams);
        if (jobId) next.set("job", jobId);
        else next.delete("job");
        setSearchParams(next, { replace: false });
    };

    const toggleGroup = (date: string) => {
        setCollapsedGroups((prev) => ({ ...prev, [date]: !prev[date] }));
    };

    const toggleActivity = (activityId: string) => {
        setCollapsedActivities((prev) => ({ ...prev, [activityId]: !prev[activityId] }));
    };

    const totalApplications = applications?.length ?? 0;

    if (loadError && !applications?.length) {
        return (
            <AuthLayout>
                <div className="max-w-xl mx-auto py-12">
                    <Link to="/candidate-investigator" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
                        <ArrowLeftIcon className="w-4 h-4" /> Back to search
                    </Link>
                    <Card className="border-red-200 bg-red-50">
                        <CardContent className="p-8 text-center">
                            <Link2OffIcon className="w-10 h-10 text-red-500 mx-auto mb-3" />
                            <h2 className="text-lg font-bold text-red-700 mb-2">Candidate not found.</h2>
                            <p className="text-sm text-red-700">{loadError}</p>
                        </CardContent>
                    </Card>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <div className="space-y-5">
                <Link
                    to="/candidate-investigator"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                    <ArrowLeftIcon className="w-4 h-4" /> Back to search
                </Link>

                <Card>
                    <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1 min-w-0 space-y-1.5">
                            <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                                {issues?.user?.name || navState?.name || (isLoadingIssues ? "Loading…" : "(name unavailable)")}
                            </h1>
                            {(issues?.user?.email || navState?.email) && (
                                <p className="text-sm text-neutral-600">{issues?.user?.email || navState?.email}</p>
                            )}
                            {(issues?.user?.phone || navState?.phone) && (
                                <p className="text-sm text-neutral-500">
                                    {(() => {
                                        const cc = issues?.user?.country_code || navState?.country_code || "";
                                        return cc ? `+${cc.replace(/^\+/, "")} ` : "";
                                    })()}
                                    {issues?.user?.phone || navState?.phone}
                                </p>
                            )}
                            <div className="flex items-center gap-2 flex-wrap pt-1">
                                <span className="text-[11px] uppercase tracking-wider text-neutral-400 font-medium">User ID</span>
                                <CopyButton value={candidateId || ""} label="user id" />
                            </div>
                            <p className="text-xs text-neutral-500">
                                {isLoadingApplications
                                    ? "Loading applications…"
                                    : `${totalApplications} application${totalApplications === 1 ? "" : "s"}`}
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 sm:items-end">
                            {isLoadingCv ? (
                                <div className="h-5 w-40 bg-neutral-100 rounded animate-pulse" />
                            ) : cv?.cv_url ? (
                                <a
                                    href={cv.cv_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                    <FileIcon className="w-4 h-4" />
                                    <span className="truncate max-w-[200px]">{cv.filename || "Download CV"}</span>
                                    <DownloadIcon className="w-3.5 h-3.5" />
                                </a>
                            ) : (
                                <span className="text-xs text-neutral-400">No CV uploaded</span>
                            )}
                            {cv?.uploaded_at && (
                                <span className="text-[11px] text-neutral-400">
                                    Uploaded {parseFormat(cv.uploaded_at, "D MMM YYYY")}
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <IssueSummaryRow issues={issues} isLoading={isLoadingIssues} />

                {issues && issues.bug_reports.length > 0 && (
                    <BugReportsSection reports={issues.bug_reports} />
                )}

                {issues && issues.interview_feedback.length > 0 && (
                    <InterviewFeedbackSection feedback={issues.interview_feedback} />
                )}

                {issues && issues.problem_activities.length > 0 && (
                    <ProblemActivitiesSection activities={issues.problem_activities} />
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
                    {/* Applications list */}
                    <div className="space-y-2">
                        <p className="text-xs uppercase tracking-wider text-neutral-400 font-medium">Applications</p>
                        {isLoadingApplications ? (
                            <Card>
                                <CardContent className="p-4">
                                    <div className="h-4 w-3/4 bg-neutral-100 rounded animate-pulse mb-2" />
                                    <div className="h-3 w-1/2 bg-neutral-100 rounded animate-pulse" />
                                </CardContent>
                            </Card>
                        ) : !applications || applications.length === 0 ? (
                            <Card>
                                <CardContent className="p-6 text-center text-sm text-neutral-500">
                                    No applications yet.
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {applications.map((app) => {
                                    const isSelected = app.job_id === selectedJobId;
                                    const hasInterview = !!app.assessment_ids?.find((a) => a.type === "Interview");
                                    const hasForm = !!app.assessment_ids?.find((a) => a.type === "Form");
                                    const statusBadge = getApplicantStatusBadge(convertToLowerSnakeCase(app.applicant_status || ""));
                                    const companyName = issues?.applications?.find((a) => a.job_id === app.job_id)?.company_name || null;
                                    return (
                                        <button
                                            key={app.job_id}
                                            type="button"
                                            onClick={() => selectApplication(app.job_id)}
                                            className={`w-full text-left rounded-lg border transition-colors ${
                                                isSelected
                                                    ? "border-blue-400 bg-blue-50"
                                                    : "border-neutral-200 bg-white hover:border-neutral-300"
                                            }`}
                                        >
                                            <div className="p-4 space-y-2">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-sm font-semibold text-gray-900 line-clamp-2">
                                                        {app.job_title}
                                                    </p>
                                                    <span className={cn(
                                                        "text-[11px] font-medium rounded-full px-2 py-0.5 flex-shrink-0",
                                                        statusBadge.className
                                                    )}>
                                                        {statusBadge.label || app.applicant_status}
                                                    </span>
                                                </div>
                                                {companyName && (
                                                    <p className="text-[11px] text-neutral-600 inline-flex items-center gap-1">
                                                        <BuildingIcon className="w-3 h-3 text-neutral-400" />
                                                        <span className="truncate">{companyName}</span>
                                                    </p>
                                                )}
                                                <div className="flex items-center flex-wrap gap-2 text-[11px]">
                                                    {hasInterview && (
                                                        <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                                                            <VideoIcon className="w-3 h-3" /> AI Interview
                                                        </span>
                                                    )}
                                                    {hasForm && (
                                                        <span className="inline-flex items-center gap-1 text-purple-700 bg-purple-50 border border-purple-100 px-1.5 py-0.5 rounded">
                                                            <FileTextIcon className="w-3 h-3" /> Form
                                                        </span>
                                                    )}
                                                    {app.is_has_work_authorization && (
                                                        <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded">
                                                            <ShieldCheckIcon className="w-3 h-3" /> Eligibility
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[11px] text-neutral-500">
                                                    Applied {app.applied_at ? parseFormat(app.applied_at, "D MMM YYYY") : "—"}
                                                </p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Investigation panel */}
                    <div className="space-y-4">
                        {!selectedApplication ? (
                            <Card>
                                <CardContent className="p-10 text-center text-sm text-neutral-500">
                                    <BriefcaseIcon className="w-8 h-8 text-neutral-300 mx-auto mb-2" />
                                    Select an application from the left to start investigating.
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                <SectionCard icon={BriefcaseIcon} title="Overview">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Field label="Job Title">{selectedApplication.job_title}</Field>
                                        <Field label="Applicant Status">
                                            {(() => {
                                                const badge = getApplicantStatusBadge(convertToLowerSnakeCase(selectedApplication.applicant_status || ""));
                                                return (
                                                    <span className={cn(
                                                        "text-xs font-medium rounded-full px-2 py-0.5 inline-flex items-center",
                                                        badge.className
                                                    )}>
                                                        {badge.label || selectedApplication.applicant_status}
                                                    </span>
                                                );
                                            })()}
                                        </Field>
                                        <Field label="Applied At">
                                            {selectedApplication.applied_at
                                                ? parseFormat(selectedApplication.applied_at, "D MMM YYYY HH:mm")
                                                : "—"}
                                        </Field>
                                        <Field label="Job ID">
                                            <CopyButton value={selectedApplication.job_id} label="job id" />
                                        </Field>
                                        <Field label="Applicant ID">
                                            <CopyButton value={selectedApplication.applicant_id || candidateId || ""} label="applicant id" />
                                        </Field>
                                        <Field label="Screenings configured">
                                            {selectedApplication.assessment_ids?.length ? (
                                                <div className="space-y-2">
                                                    {selectedApplication.assessment_ids.map((a) => (
                                                        <div key={a.id} className="space-y-1">
                                                            <div className="flex items-baseline gap-2 text-xs">
                                                                <span className="font-medium text-gray-700">{a.type}:</span>
                                                                <span className="text-gray-900">{a.name || "—"}</span>
                                                            </div>
                                                            <CopyButton value={a.id} label={`${a.type} id`} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-neutral-500">None</span>
                                            )}
                                        </Field>
                                    </div>
                                </SectionCard>

                                {(() => {
                                    const debugApp = issues?.applications?.find((a) => a.job_id === selectedApplication.job_id);
                                    return debugApp ? <DebugInfoSection app={debugApp} /> : null;
                                })()}

                                <SectionCard icon={HistoryIcon} title="Activity Log">
                                    {isLoadingActivity ? (
                                        <div className="space-y-2">
                                            <div className="h-4 w-1/2 bg-neutral-100 rounded animate-pulse" />
                                            <div className="h-4 w-2/3 bg-neutral-100 rounded animate-pulse" />
                                        </div>
                                    ) : !activityGroups || activityGroups.length === 0 ? (
                                        <p className="text-sm text-gray-600 dark:text-gray-400">No activity history available.</p>
                                    ) : (
                                        <div className="space-y-8">
                                            {activityGroups.map((item, groupIndex) => (
                                                <div key={groupIndex} className="space-y-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleGroup(item.date)}
                                                        className="flex items-center gap-2 w-full text-left group hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-md px-2 py-1 -mx-2 transition-colors"
                                                    >
                                                        {collapsedGroups[item.date] ? (
                                                            <ChevronRight className="h-4 w-4 text-gray-400" />
                                                        ) : (
                                                            <ChevronDown className="h-4 w-4 text-gray-400" />
                                                        )}
                                                        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                            {formatShortDate(item.date)}
                                                        </h5>
                                                        <span className="text-xs text-gray-400 dark:text-gray-500">
                                                            ({item.entries.length}{" "}
                                                            {item.entries.length === 1 ? "activity" : "activities"})
                                                        </span>
                                                    </button>
                                                    {!collapsedGroups[item.date] && (
                                                        <div className="space-y-5 pl-6">
                                                            {(item.entries || [])
                                                                .slice()
                                                                .sort((a, b) => compareIsoDatesDesc(a.action_at, b.action_at))
                                                                .map((activity, activityIndex) => {
                                                                    const activityKey = `${groupIndex}-${activityIndex}`;
                                                                    const noHeader = !activity.action_actor && !activity.action_header;
                                                                    return (
                                                                        <div key={activityKey} className="space-y-3">
                                                                            {noHeader ? (
                                                                                <>
                                                                                    <div className="flex gap-3 group items-start">
                                                                                        <div className="flex-shrink-0">
                                                                                            <div
                                                                                                className="w-9 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors py-2.5"
                                                                                                style={{ paddingRight: "10px", paddingLeft: "10px" }}
                                                                                            >
                                                                                                {activity.action_type === "email" ? (
                                                                                                    <MailIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                                                ) : activity.action_type === "status_change" ? (
                                                                                                    <ArrowRightLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                                                ) : activity.action_type === "manual" ? (
                                                                                                    <UserCheck className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                                                ) : activity.action_type === "system" ? (
                                                                                                    <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                                                ) : null}
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
                                                                                            <div className="flex-1 min-w-0">
                                                                                                <p
                                                                                                    className="text-sm text-gray-900 dark:text-gray-100 mb-1"
                                                                                                    dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(renameAssessmentToScreening(activity.action_title) || "Unknown action") }}
                                                                                                />
                                                                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                                                    {formatTimeAgo(activity.action_at)} • {parseFormat(activity.action_at, "h:mm A")}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div className="flex-shrink-0">
                                                                                                <Badge
                                                                                                    variant="secondary"
                                                                                                    className={
                                                                                                        activity.action_type === "email"
                                                                                                            ? "text-xs bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300 flex-shrink-0"
                                                                                                            : activity.action_type === "status_change"
                                                                                                                ? "text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 flex-shrink-0"
                                                                                                                : activity.action_type === "manual"
                                                                                                                    ? "text-xs bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 flex-shrink-0"
                                                                                                                    : activity.action_type === "system"
                                                                                                                        ? "text-xs bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300 flex-shrink-0"
                                                                                                                        : ""
                                                                                                    }
                                                                                                    style={{
                                                                                                        paddingTop: "4px",
                                                                                                        paddingBottom: "4px",
                                                                                                        paddingLeft: "8px",
                                                                                                        paddingRight: "8px",
                                                                                                        border: "0.909091px solid rgba(0, 0, 0, 0)",
                                                                                                        borderRadius: "1rem",
                                                                                                        boxShadow: "none",
                                                                                                    }}
                                                                                                >
                                                                                                    {convertCapitalizeWords(activity.action_type)}
                                                                                                </Badge>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                    {activity.action_content && (
                                                                                        <div className="flex gap-3">
                                                                                            <div className="w-9 flex-shrink-0" />
                                                                                            <div className="flex-1">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => toggleActivity(`${activityKey}-note`)}
                                                                                                    className="flex items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                                                                                >
                                                                                                    {collapsedActivities[`${activityKey}-note`] ? (
                                                                                                        <ChevronRight className="h-3 w-3" />
                                                                                                    ) : (
                                                                                                        <ChevronDown className="h-3 w-3" />
                                                                                                    )}
                                                                                                    <span className="font-medium">
                                                                                                        {activity.action_label || "Note"}
                                                                                                    </span>
                                                                                                </button>
                                                                                                {!collapsedActivities[`${activityKey}-note`] && (
                                                                                                    <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-md p-3 border-l-2 border-green-300 dark:border-green-700">
                                                                                                        {typeof activity.action_content === "string" && (
                                                                                                            <p
                                                                                                                className="whitespace-pre-line"
                                                                                                                dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(activity.action_content || "") }}
                                                                                                            />
                                                                                                        )}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <div className="flex items-start justify-between gap-4 group">
                                                                                        <div className="flex-shrink-0">
                                                                                            {activity.action_actor !== "Candidate" ? (
                                                                                                <div className="w-[52px] h-[52px] rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                                                                                    <BuildingIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div className="w-[52px] h-[52px] rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                                                                                    <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                                                                                {renameAssessmentToScreening(activity.action_header) || "Unknown Action"}
                                                                                            </p>
                                                                                            <p
                                                                                                className="text-sm text-gray-600 dark:text-gray-400 mb-2"
                                                                                                dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(renameAssessmentToScreening(activity.action_description)) }}
                                                                                            />
                                                                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                                                {parseFormat(activity.action_at, "D MMM YYYY")} • {parseFormat(activity.action_at, "h:mm A")}
                                                                                                {activity.action_actor ? ` • ${activity.action_actor}` : ""}
                                                                                            </p>
                                                                                        </div>
                                                                                        <div className="flex-shrink-0 text-right">
                                                                                            {activity.action_stage && (
                                                                                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                                                    Stage: <span className="font-semibold">{activity.action_stage.map((stage) => mapApplicantStage[stage] || renameAssessmentToScreening(stage)).join(" → ")}</span>
                                                                                                </p>
                                                                                            )}
                                                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                                                {activity.action_actor === "Candidate" ? activity.action_actor : convertCapitalizeWords(activity.action_type)}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                    {activity.action_content && typeof activity.action_content !== "string" && activity.action_content.length > 0 && activity.action_content.map((contentItem, contentIndex) => (
                                                                                        <div className="flex gap-3" key={contentIndex}>
                                                                                            <div className="w-9 flex-shrink-0" />
                                                                                            <div className="flex-1">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => toggleActivity(`${activityKey}-${contentIndex}-note`)}
                                                                                                    className="flex items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                                                                                                >
                                                                                                    {collapsedActivities[`${activityKey}-${contentIndex}-note`] ? (
                                                                                                        <ChevronRight className="h-3 w-3" />
                                                                                                    ) : (
                                                                                                        <ChevronDown className="h-3 w-3" />
                                                                                                    )}
                                                                                                    <span className="font-medium">
                                                                                                        {contentItem.label || "Note"}
                                                                                                    </span>
                                                                                                </button>
                                                                                                {!collapsedActivities[`${activityKey}-${contentIndex}-note`] && (
                                                                                                    <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-md p-3 border-l-2 border-green-300 dark:border-green-700">
                                                                                                        <p
                                                                                                            className="whitespace-pre-line"
                                                                                                            dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(contentItem.content || "") }}
                                                                                                        />
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </SectionCard>

                                {selectedApplication.assessment_ids?.find((a) => a.type === "Interview") && (
                                    <SectionCard icon={VideoIcon} title="AI Interview">
                                        {isLoadingInterview ? (
                                            <div className="h-4 w-1/2 bg-neutral-100 rounded animate-pulse" />
                                        ) : !interviewSubmission || !interviewSubmission.id ? (
                                            <p className="text-sm text-neutral-500">No interview submission yet.</p>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <Field label="Status">{interviewSubmission.status || "—"}</Field>
                                                    <Field label="Last update">
                                                        {interviewSubmission.updated_at
                                                            ? parseFormat(interviewSubmission.updated_at, "D MMM YYYY HH:mm")
                                                            : "—"}
                                                    </Field>
                                                    <Field label="Submission ID">
                                                        <CopyButton value={interviewSubmission.id} label="submission id" />
                                                    </Field>
                                                    {interviewSubmission.conversation_id && (
                                                        <Field label="Conversation ID">
                                                            <CopyButton
                                                                value={interviewSubmission.conversation_id}
                                                                label="conversation id"
                                                            />
                                                        </Field>
                                                    )}
                                                </div>
                                                {interviewSubmission.conversation_id && (
                                                    <InterviewPlayer
                                                        isLoadingVideo={isLoadingVideo}
                                                        interviewStatus={interviewStatus}
                                                        s3VideoLink={videoInterview?.s3_link || ""}
                                                        videoInterview={videoInterview as InterviewVideo}
                                                        setVideoDuration={() => undefined}
                                                    />
                                                )}
                                                {interviewSubmission.conversation_id && (
                                                    <InterviewTranscriptionPanel
                                                        transcription={transcription}
                                                        isLoading={isLoadingTranscription}
                                                        error={transcriptionError}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </SectionCard>
                                )}

                                {selectedApplication.assessment_ids?.find((a) => a.type === "Form") && (
                                    <SectionCard icon={FileTextIcon} title="Form">
                                        {isLoadingForm ? (
                                            <div className="h-4 w-1/2 bg-neutral-100 rounded animate-pulse" />
                                        ) : !formSubmission || !formSubmission.id ? (
                                            <p className="text-sm text-neutral-500">No form submission yet.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <Field label="Status">{formSubmission.status || "—"}</Field>
                                                <Field label="Last update">
                                                    {formSubmission.updated_at
                                                        ? parseFormat(formSubmission.updated_at, "D MMM YYYY HH:mm")
                                                        : "—"}
                                                </Field>
                                                <Field label="Submission ID">
                                                    <CopyButton value={formSubmission.id} label="submission id" />
                                                </Field>
                                                <Field label="Answers">
                                                    {Array.isArray(formSubmission.answers)
                                                        ? `${formSubmission.answers.length} answer${formSubmission.answers.length === 1 ? "" : "s"}`
                                                        : "—"}
                                                </Field>
                                            </div>
                                        )}
                                    </SectionCard>
                                )}

                                {selectedApplication.is_has_work_authorization && (
                                    <SectionCard icon={ShieldCheckIcon} title="Eligibility / Work Authorization">
                                        <SWEExperienceSurvey
                                            is_view
                                            work_authorization_metadata={selectedApplication.work_authorization_metadata}
                                            elligility_answers={selectedApplication.elligibility_answers}
                                            location_metadata={selectedApplication.location_metadata}
                                        />
                                    </SectionCard>
                                )}

                                <Card className="border-amber-100 bg-amber-50">
                                    <CardContent className="p-4 flex items-start gap-3">
                                        <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-xs text-amber-800">
                                            All data shown here is read-only. Use the copy buttons to grab IDs for
                                            cross-referencing in Mixpanel, Sentry, or backend logs.
                                            <Button
                                                variant="link"
                                                className="px-1 h-auto text-amber-900 underline"
                                                onClick={() => {
                                                    const payload = JSON.stringify(
                                                        {
                                                            user_id: candidateId,
                                                            job_id: selectedApplication.job_id,
                                                            applicant_id: selectedApplication.applicant_id || candidateId,
                                                            assessment_ids: selectedApplication.assessment_ids,
                                                            interview_submission_id: interviewSubmission?.id,
                                                            conversation_id: interviewSubmission?.conversation_id,
                                                            form_submission_id: formSubmission?.id,
                                                        },
                                                        null,
                                                        2
                                                    );
                                                    navigator.clipboard.writeText(payload).then(() => {
                                                        setDebugCopied(true);
                                                        setTimeout(() => setDebugCopied(false), 1500);
                                                    });
                                                }}
                                            >
                                                {debugCopied ? "Copied!" : "Copy debug JSON"}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
}
